import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { MachineOption, useMachineOptions } from '../select/MachineSelect';
import { SubprocessOption } from '../select/SubprocessCells';
import { machineRepairKey, RNMachineRepair, RNMachineRepairField } from '../types/rnMachineRepairs';
import RNSubprocessMachineRow from './RNSubprocessMachineRow';
import MachineRepairRequestModal from './MachineRepairRequestModal';

interface Props {
  activeSubprocess?: SubprocessOption;
  repairs: RNMachineRepair[];
  onChange: (subprocessId: string, machineId: string, field: RNMachineRepairField, value: string) => void;
  onApprove: (subprocessId: string, machineId: string, next: boolean) => void;
  approvingKey: string | null;
  dirty: boolean;
  readOnly: boolean;
  canApprove: boolean;
  rnWorkOrderId?: string;
  rnWorkOrderNumber?: string;
  initialSubprocessId?: string;
  initialMachineId?: string;
  initialRepairRequestId?: string;
  /** Card body hidden; the title bar stays and toggles it (replaces a separate show/hide button). */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

export const formatRU = (value: MachineOption['ru'], language: string) => {
  const docs = Array.isArray(value) ? value : value ? [value] : [];
  return docs.map((doc) => {
    if (!doc || typeof doc === 'string') return '';
    const name = language === 'en' ? doc.nameEN || doc.nameMK : doc.nameMK || doc.nameEN;
    return [doc.code, name].filter(Boolean).join(' - ');
  }).filter(Boolean).join(', ');
};

// РУ (монтажа и демонтажа) и РУ (ракување/чистење) се сопствени колони — read-only,
// влечени од шифрарникот на машината. Потписот е една состојба наместо три колони.
const COLUMNS: Array<[string, number]> = [
  ['machine', 200], ['ruAssembly', 230], ['ruHandling', 230], ['approval', 200],
  ['repair', 260], ['repairDate', 145], ['repairBy', 160],
];

const RNSubprocessMachinesTable: React.FC<Props> = ({ activeSubprocess, repairs, onChange, onApprove, approvingKey, dirty, readOnly, canApprove, rnWorkOrderId, rnWorkOrderNumber, initialSubprocessId, initialMachineId, initialRepairRequestId, collapsed = false, onToggleCollapsed }) => {
  const { t, i18n } = useTranslation();
  const machineOptions = useMachineOptions();
  const inputRefs = useRef<(HTMLInputElement | null)[][]>([]);
  const [selectedMachineIds, setSelectedMachineIds] = useState<Set<string>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const handledRepairRequestRef = useRef('');
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const machineById = useMemo(() => new Map(machineOptions.map((machine) => [machine._id, machine])), [machineOptions]);
  const rows = useMemo(() => [...(activeSubprocess?.machines || [])]
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
    .map((entry) => {
      const machineRef = entry.machine;
      const machineId = typeof machineRef === 'object' && machineRef ? machineRef._id : String(machineRef || '');
      return { machineId, machine: machineById.get(machineId) };
    })
    .filter((row) => !!row.machineId), [activeSubprocess, machineById]);
  const repairByKey = useMemo(() => new Map(repairs.map((repair) => [machineRepairKey(repair.subprocessId, repair.machineId), repair])), [repairs]);

  // Drop stale IDs when the subprocess / rows change.
  useEffect(() => {
    const rowIds = new Set(rows.map((row) => row.machineId));
    setSelectedMachineIds((prev) => {
      const next = new Set([...prev].filter((id) => rowIds.has(id)));
      return next.size !== prev.size ? next : prev;
    });
  }, [rows]);

  // Deep-link: auto-check one machine and open modal.
  useEffect(() => {
    if (!initialRepairRequestId || handledRepairRequestRef.current === initialRepairRequestId) return;
    if (!activeSubprocess || activeSubprocess._id !== initialSubprocessId || !initialMachineId) return;
    if (!rows.some((row) => row.machineId === initialMachineId)) return;
    handledRepairRequestRef.current = initialRepairRequestId;
    setSelectedMachineIds(new Set([initialMachineId]));
    setModalOpen(true);
  }, [activeSubprocess, initialMachineId, initialRepairRequestId, initialSubprocessId, rows]);

  // Keep indeterminate state in sync.
  const allChecked = rows.length > 0 && rows.every((row) => selectedMachineIds.has(row.machineId));
  const someChecked = !allChecked && rows.some((row) => selectedMachineIds.has(row.machineId));
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someChecked;
  }, [someChecked]);

  // Empty states are cards, not nothing: a hidden table reads as a broken screen.
  if (!activeSubprocess) {
    return <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4 max-w-full">
      <div className="themed-gradient px-5 py-2 rounded-t-xl"><h2 className="text-white font-semibold text-sm">{t('rnMachineRepairs.title')}</h2></div>
      <div className="p-4 text-[11px] text-gray-400 text-center">{t('rnMachineRepairs.pickSubprocess')}</div>
    </div>;
  }

  const focus = (row: number, col: number) => inputRefs.current[row]?.[col]?.focus();
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, row: number, col: number) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter') { event.preventDefault(); focus(row + 1, col); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); focus(row - 1, col); }
  };

  const toggleMachine = (machineId: string) => {
    setSelectedMachineIds((prev) => {
      const next = new Set(prev);
      if (next.has(machineId)) next.delete(machineId); else next.add(machineId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedMachineIds(allChecked ? new Set() : new Set(rows.map((row) => row.machineId)));
  };

  // Build the machines list to pass to the modal (preserves row order).
  const selectedMachines = rows
    .filter((row) => selectedMachineIds.has(row.machineId))
    .map((row) => ({
      id: row.machineId,
      name: row.machine ? `${row.machine.code} - ${row.machine.name}` : row.machineId,
    }));

  const canSendRequest = !readOnly && !!rnWorkOrderId && selectedMachineIds.size > 0;

  return <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4 max-w-full">
    <div className={`themed-gradient px-5 py-2 flex items-center justify-between gap-3 ${collapsed ? 'rounded-xl' : 'rounded-t-xl'}`}>
      <button
        type="button"
        onClick={onToggleCollapsed}
        disabled={!onToggleCollapsed}
        aria-expanded={!collapsed}
        title={collapsed ? t('rnMachineRepairs.show') || undefined : t('rnMachineRepairs.hide') || undefined}
        className="flex items-center gap-2 text-left focus:outline-none focus:ring-2 focus:ring-white/60 rounded disabled:cursor-default"
      >
        {onToggleCollapsed && (collapsed ? <ChevronRight size={16} className="text-white" /> : <ChevronDown size={16} className="text-white" />)}
        <h2 className="text-white font-semibold text-sm">{t('rnMachineRepairs.title')} — {activeSubprocess.name || ''}</h2>
      </button>
      {!collapsed && <button
        type="button"
        onClick={() => setModalOpen(true)}
        disabled={!canSendRequest}
        title={selectedMachineIds.size === 0 ? t('machineRepairRequests.selectMachines') || undefined : undefined}
        className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium shadow-sm focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 ${
          canSendRequest ? 'border-blue-200 bg-white text-blue-700 hover:bg-blue-50' : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
      >
        {t('machineRepairRequests.sendRequest')}
      </button>}
    </div>
    {!collapsed && <div className="overflow-x-auto rounded-b-xl"><table className="border-collapse w-full min-w-[1465px]"><thead><tr>
      {/* Select-all header checkbox */}
      <th className="border border-gray-300 bg-gray-200 h-[32px] px-2 text-center" style={{ minWidth: 36 }}>
        <input
          ref={selectAllRef}
          type="checkbox"
          checked={allChecked}
          onChange={toggleAll}
          className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500/50 cursor-pointer"
        />
      </th>
      {COLUMNS.map(([key, width]) => <th key={key} className="border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-center text-[11px] h-[32px] px-1.5" style={{ minWidth: width }}>{t(`rnMachineRepairs.${key}`)}</th>)}
    </tr></thead><tbody>{rows.length === 0 ? <tr><td colSpan={COLUMNS.length + 1} className="border border-gray-300 h-[30px] px-3 text-center text-[11px] text-gray-400">{t('rnMachineRepairs.empty')}</td></tr> : rows.map(({ machineId, machine }, rowIndex) => {
      const repair = repairByKey.get(machineRepairKey(activeSubprocess._id, machineId));
      const name = machine ? `${machine.code} - ${machine.name}` : machineId;
      return (
        <RNSubprocessMachineRow
          key={machineId}
          machineId={machineId}
          machineName={name}
          ruAssembly={formatRU(machine?.ru, i18n.language)}
          ruHandling={formatRU(machine?.ruHandling, i18n.language)}
          repair={repair}
          checked={selectedMachineIds.has(machineId)}
          onCheck={() => toggleMachine(machineId)}
          onChange={(field, value) => onChange(activeSubprocess._id, machineId, field, value)}
          onApprove={(next) => onApprove(activeSubprocess._id, machineId, next)}
          approving={approvingKey === machineRepairKey(activeSubprocess._id, machineId)}
          dirty={dirty}
          readOnly={readOnly}
          canApprove={canApprove}
          registerInputRef={(col, el) => { inputRefs.current[rowIndex] ||= []; inputRefs.current[rowIndex][col] = el; }}
          onKeyDown={(e, col) => onKeyDown(e, rowIndex, col)}
        />
      );
    })}</tbody></table></div>}

    <MachineRepairRequestModal
      isOpen={modalOpen}
      onClose={() => setModalOpen(false)}
      rnWorkOrderId={rnWorkOrderId || ''}
      rnWorkOrderNumber={rnWorkOrderNumber || ''}
      subprocessId={activeSubprocess._id}
      subprocessName={activeSubprocess.name || ''}
      machines={selectedMachines}
    />
  </div>;
};

export default RNSubprocessMachinesTable;
